import { useState, useCallback } from 'react';
import { useSelection } from '../contexts/SelectionContext';
import { xyzPointSelection } from '../lib/mapSelection';
import { rawPopupProps } from './useDatasetRawLayers';
import { readingSelection } from '../components/pseudosection/pseudosection-helpers';

/**
 * Selection + hover state for MapView: selected line/anomaly/XYZ point, chart
 * highlight, cursor and coordinate readout, and the click / mousemove handlers.
 */
export function useMapSelection({ tool, activeDrawMode, layers, importedLayers, resultLayers = [], onToolClick }) {
  const { setSelection } = useSelection();
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [xyzHover, setXyzHover] = useState(null);       // hover tooltip for XYZ points
  const [xyzPopup, setXyzPopup] = useState(null);       // click popup with full row
  const [chartSelection, setChartSelection] = useState(null); // { layerId, properties }
  const [cursor, setCursor] = useState('default');
  const [mouseCoord, setMouseCoord] = useState(null);

  // Show the XYZ row popup. M14: also clears the line/anomaly selections, which
  // otherwise out-rank the XYZ popup (priority anomaly || line || xyz).
  const showXyzPopup = useCallback((point) => {
    const next = xyzPointSelection(point);
    setSelectedFeature(next.selectedFeature);
    setSelectedAnomaly(next.selectedAnomaly);
    setXyzPopup(next.xyzPopup);
  }, []);

  const clearPopups = useCallback(() => {
    setSelectedFeature(null);
    setSelectedAnomaly(null);
    setXyzPopup(null);
  }, []);

  // Map interaction
  const handleMapClick = useCallback((evt) => {
    // Drawing is handled by Terra Draw — ignore feature-selection clicks while drawing
    if (activeDrawMode) return;

    // Measure / annotate tools consume the click
    if (onToolClick(evt)) return;

    // Check anomalies first
    const anomalyFeatures = evt.features?.filter(f => f.layer?.id === 'anomaly-circles');
    if (anomalyFeatures?.length > 0) {
      const props = anomalyFeatures[0].properties;
      setSelectedAnomaly({
        longitude: evt.lngLat.lng, latitude: evt.lngLat.lat,
        ...props,
      });
      setSelectedFeature(null);
      return;
    }
    // Check lines
    const lineFeatures = evt.features?.filter(f => f.layer?.id === 'survey-lines');
    if (lineFeatures?.length > 0) {
      const props = lineFeatures[0].properties;
      setSelectedFeature({
        longitude: evt.lngLat.lng, latitude: evt.lngLat.lat,
        ...props,
        _geometry: lineFeatures[0].geometry,
      });
      setSelectedAnomaly(null);
      return;
    }
    // Check USGS tracklines (real data) — enables "View in 3D"
    const usgsLine = evt.features?.find(f => (f.layer?.id || '').startsWith('usgs-line-'));
    if (usgsLine) {
      const lid = usgsLine.layer.id.slice('usgs-line-'.length);
      const srcLayer = layers.find(l => l.id === lid);
      const lineName = usgsLine.properties?.name || usgsLine.properties?.LINE_ID || usgsLine.properties?.lineid || `${srcLayer?.name || 'Trackline'}`;
      setSelectedFeature({
        longitude: evt.lngLat.lng, latitude: evt.lngLat.lat,
        name: lineName,
        method: srcLayer?.method,
        isTrackline: true,
        ...usgsLine.properties,
        _geometry: usgsLine.geometry,
      });
      setSelectedAnomaly(null);
      // Cross-view selection sync (GIS ↔ Section ↔ 3D)
      if (usgsLine.geometry?.type === 'LineString') {
        setSelection({
          type: 'line', name: lineName, source: 'map',
          coordinates: usgsLine.geometry.coordinates,
          position: [evt.lngLat.lng, evt.lngLat.lat],
        });
      }
      return;
    }
    // Check soundings
    const soundingFeatures = evt.features?.filter(f => f.layer?.id === 'soundings');
    if (soundingFeatures?.length > 0) {
      const props = soundingFeatures[0].properties;
      setSelectedFeature({
        longitude: evt.lngLat.lng, latitude: evt.lngLat.lat,
        ...props,
      });
      setSelectedAnomaly(null);
      return;
    }
    // Check chartable data layers (USGS / imported / result points) → chart highlight
    const dataFeature = evt.features?.find(f => {
      const lid = f.layer?.id || '';
      return lid.startsWith('usgs-pts-') || lid.startsWith('imp-pts-') || lid.startsWith('result-pts-');
    });
    if (dataFeature) {
      const lid = dataFeature.layer.id;
      const chartLayerId = lid.startsWith('usgs-pts-') ? `usgs-${lid.slice('usgs-pts-'.length)}`
        : lid.startsWith('imp-pts-') ? lid.slice('imp-pts-'.length)
        : lid.slice('result-pts-'.length);
      setChartSelection({ layerId: chartLayerId, properties: dataFeature.properties });
      // Imported layer → attribute popup: full row for XYZ imports (Z fields highlighted),
      // the feature's properties for GeoJSON/KML/GPX points (V.47)
      if (lid.startsWith('imp-pts-')) {
        const lay = importedLayers.find(l => l.id === lid.slice('imp-pts-'.length));
        if (lay) {
          showXyzPopup({
            longitude: evt.lngLat.lng, latitude: evt.lngLat.lat,
            properties: dataFeature.properties || {}, zFields: lay.xyz?.zFields || [],
          });
          return;
        }
      }
      // Dataset-backed raw survey layers (electrodes / pseudosection) → reading popup
      if (lid.startsWith('result-pts-')) {
        const rl = resultLayers.find(r => r.id === chartLayerId && r.dataset);
        if (rl) {
          // Link to the pseudosection panel: the same reading is highlighted there
          if (rl.dataset.kind === 'pseudosection' && Number.isInteger(dataFeature.properties?.seq)) {
            setSelection(readingSelection(rl.dataset.id, dataFeature.properties.seq, 'map'));
          }
          showXyzPopup({
            longitude: evt.lngLat.lng, latitude: evt.lngLat.lat,
            properties: rawPopupProps(rl.dataset.kind, dataFeature.properties), zFields: ['apparent_resistivity'],
          });
          return;
        }
      }
      setXyzPopup(null);
      return;
    }
    setSelectedFeature(null);
    setSelectedAnomaly(null);
    setChartSelection(null);
    setXyzPopup(null);
  }, [activeDrawMode, onToolClick, layers, importedLayers, resultLayers, setSelection, showXyzPopup]);

  const handleMouseMove = useCallback((evt) => {
    const features = evt.features || [];
    setCursor(features.length > 0 ? 'pointer' : (tool === 'pan' ? 'grab' : 'default'));
    setMouseCoord({ lon: evt.lngLat.lng, lat: evt.lngLat.lat });
    // XYZ hover tooltip
    const xyzFeat = features.find(ft => (ft.layer?.id || '').startsWith('imp-pts-'));
    if (xyzFeat) {
      const lid = xyzFeat.layer.id.slice('imp-pts-'.length);
      const lay = importedLayers.find(l => l.id === lid && l.xyz);
      if (lay) {
        setXyzHover({ x: evt.point.x, y: evt.point.y, props: xyzFeat.properties, zFields: lay.xyz.zFields });
        return;
      }
    }
    setXyzHover(null);
  }, [tool, importedLayers]);

  return {
    selectedFeature, setSelectedFeature, selectedAnomaly, setSelectedAnomaly,
    xyzHover, xyzPopup, showXyzPopup, chartSelection, cursor: tool === 'pan' && cursor === 'default' ? 'grab' : cursor, mouseCoord,
    handleMapClick, handleMouseMove, clearPopups,
  };
}
